import React, { useState, useEffect } from "react";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  ADD_CERTIFICATIONS_LABEL,
  CANCEL_LABEL,
  CATEGORY_LABEL,
  CERTIFICATION_LABEL,
  CERTIFICATIONS_LABEL,
  CONFIRM_LABEL,
  DESCRIPTION_LABEL,
  EMPTY_CERTIFICATIONS_MESSAGE,
  EMPTY_STRING,
  EXECUTION_MODE_LABEL,
  GROUP_TARGETS_LABEL,
  INTERNAL_GRADE_IT_LABEL,
  NAME_LABEL,
  RECIPIENT_LABEL,
  URL_LABEL,
} from "../../../../../../../constants/Dictionary";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import { BodyTyp } from "../../../../../../../components/Typography/Typography";
import {
  AlignmentEnum,
  FontWeightEnum,
} from "../../../../../../../types/AppTypes";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { ReactComponent as CancelIcon } from "../../../../../../../icons/close.svg";
import { TableFormationCM } from "../../TableFormationCM/TableFormationCM";
import { useDebounce } from "use-debounce";
import { CERTIFICATIONS_PAGE_SIZE } from "../../../../../../../constants/AppConstants";
import { Certification } from "../../../../../../../types/api/certifications.type";
import { StyledFormationCM } from "../../TableFormationCM/TableFormationCM.styled";
import { DoubleStructure } from "../../../../../../../components/core/DoubleStructure/DoubleStructure";
import SearchInput from "../../../../../../../components/core/SearchInput/SearchInput";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../../../components/core/Select/Select";
import { CollaboratorCmSheet } from "../../../../../../../types/api/collaborators.type";
import { getCollaboratorCertificationsModalColumns } from "../../../../../../../constants/TableColumns";
import {
  TextBox,
  TextBoxGrid,
} from "../../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { useAppSelector } from "../../../../../../../hooks/store.hook";
import TargetGroups from "../../../../../../../components/TargetGroups/TargetGroups";
import { findLabelFromValue } from "../../../../../../../utils/orgFunctions.utils";
import { useCmSheetAvailableCertifications } from "../../../../../../../hooks/cmSheet/certifications.hook";

interface AddCollaboratorCertificationProps {
  isOpen: boolean;
  closeModal: () => void;
  onConfirm: (certifications: Certification[]) => void;
  cmSheetInfo: CollaboratorCmSheet;
}

export const AddCollaboratorCertification = ({
  isOpen,
  closeModal,
  onConfirm,
  cmSheetInfo,
}: AddCollaboratorCertificationProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [pageNumber, setPageNumber] = useState(0);
  const [certificationsSaved, setCertificationsSaved] = useState<
    Certification[]
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const { orgSettings } = useAppSelector((state) => state.config);

  const confirmModal = (certifications: Certification[]) => {
    onConfirm(certifications);
    closeModal();
  };

  const handleCloseModal = () => {
    setSelectedCategories([]);
    closeModal();
    setCertificationsSaved([]);
  };

  const { data, isFetching } = useCmSheetAvailableCertifications({
    cmSheetId: cmSheetInfo.id,
    pageNumber,
    selectedCategories,
    searchDebouncedValue,
  });

  const dataForTable: Certification[] = data?.data ?? [];

  const handleRowSelect = (certification: Certification, checked: boolean) => {
    setCertificationsSaved((prev) => {
      if (checked) {
        return prev.some((i) => i.id === certification.id)
          ? prev
          : [
              ...prev,
              {
                ...certification,
              },
            ];
      }
      return prev.filter((i) => i.id !== certification.id);
    });
  };

  const handleSelectAll = (
    rows: Certification[],
    certificationsSaved: boolean,
  ) => {
    if (!certificationsSaved) {
      const pageIds = new Set(rows.map((r) => r.id));
      setCertificationsSaved((prev) => prev.filter((c) => !pageIds.has(c.id)));
      return;
    }
    setCertificationsSaved((prev) => {
      const existingIds = new Set(prev.map((c) => c.id));
      return [...prev, ...rows.filter((r) => !existingIds.has(r.id))];
    });
  };

  const expandRow = (record: Certification): React.ReactElement | null => {
    return (
      <>
        {!record.adHocCollaborator && (
          <TextBoxGrid
            numCol={2}
            items={[
              {
                label: GROUP_TARGETS_LABEL,
                text: <TargetGroups cc={record.cc} />,
              },
              {
                label: INTERNAL_GRADE_IT_LABEL,
                text:
                  record.internalGrade && record.internalGrade.length > 0
                    ? record.internalGrade.join(", ")
                    : EMPTY_STRING,
              },
            ]}
          />
        )}
        {record.adHocCollaborator ? (
          <TextBoxGrid
            numCol={3}
            items={[
              {
                label: RECIPIENT_LABEL,
                text: record.collaborator
                  ? `${record.collaborator.surname} ${record.collaborator.name}`
                  : EMPTY_STRING,
              },
              {
                label: EXECUTION_MODE_LABEL,
                text: findLabelFromValue(
                  orgSettings.certifications.executionModes,
                  record.executionMode,
                ),
              },
              {
                label: URL_LABEL,
                text: record.url,
              },
            ]}
          />
        ) : (
          <TextBoxGrid
            numCol={2}
            items={[
              {
                label: EXECUTION_MODE_LABEL,
                text: findLabelFromValue(
                  orgSettings.certifications.executionModes,
                  record.executionMode,
                ),
              },
              {
                label: URL_LABEL,
                text: record.url,
              },
            ]}
          />
        )}
        <TextBox label={DESCRIPTION_LABEL} text={record.description} />
      </>
    );
  };

  const rowKey = (record: Certification) => {
    return record.id.toString();
  };

  useEffect(() => {
    setPageNumber(0);
  }, [selectedCategories, searchDebouncedValue]);

  return (
    <Modal
      label={ADD_CERTIFICATIONS_LABEL}
      isOpen={isOpen}
      rightButton={
        <Button
          icon={<CheckIcon />}
          label={CONFIRM_LABEL}
          onClick={() =>
            certificationsSaved.length > 0 && confirmModal(certificationsSaved)
          }
          disabled={certificationsSaved.length === 0}
        />
      }
      leftButton={
        <Button
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          label={CANCEL_LABEL}
          onClick={handleCloseModal}
        />
      }
    >
      <StyledFormationCM $isModal>
        <div className="cm-formation-header-container">
          <DoubleStructure
            verticalAlignment={AlignmentEnum.CENTER}
            variant="primary"
            wrap="end"
          >
            <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
              {`${data?.pagination.total ?? 0} ${data && data.pagination.total === 1 ? CERTIFICATION_LABEL : CERTIFICATIONS_LABEL}`}
            </BodyTyp>
            <div className="filters">
              <SearchInput
                value={searchValue}
                onChange={setSearchValue}
                label={NAME_LABEL}
                name={"search"}
              />
              <Select
                variant={SelectVariantEnum.MULTI_SELECT}
                label={CATEGORY_LABEL}
                options={orgSettings.certifications.categories}
                onChange={setSelectedCategories}
                value={selectedCategories}
              />
            </div>
          </DoubleStructure>
        </div>
        <TableFormationCM<Certification>
          data={dataForTable}
          scrollY={"40dvh"}
          loading={isFetching}
          pagination={{
            current: pageNumber,
            onChange: setPageNumber,
            pageSize: CERTIFICATIONS_PAGE_SIZE,
            total: data?.pagination.total ?? 0,
          }}
          headers={getCollaboratorCertificationsModalColumns(orgSettings)}
          rowKey={rowKey}
          expandable={{
            expandedRowRender: expandRow,
          }}
          emptyText={EMPTY_CERTIFICATIONS_MESSAGE}
          onCheckbox={handleRowSelect}
          onSelectAll={handleSelectAll}
          selectedRows={certificationsSaved}
        />
      </StyledFormationCM>
    </Modal>
  );
};
